import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiFormDataRequest, apiV1Request } from '#shared/api/requests';
import {
    getOpenApiPromocodeGetByIdUrl,
    getOpenApiPromocodeGroupDeleteUrl,
    getOpenApiPromocodeGroupPromocodeCreateUrl,
    getOpenApiPromocodeGroupPromocodeDeleteUrl,
    getOpenApiPromocodeGroupPromocodeListUrl,
    getOpenApiPromocodeGroupPromocodesImportUrl,
    getOpenApiPromocodeGroupPromocodeUpdateUrl,
    getOpenApiPromocodeGroupSetDeactivatedUrl,
    getOpenApiPromocodeGroupUpdateUrl,
    type OpenApiGetIncentivesCountRequest,
    type OpenApiGetIncentivesCountResponseSuccess,
    type OpenApiPromocodeGetByIdPathParameters,
    type OpenApiPromocodeGetByIdResponseSuccess,
    type OpenApiPromocodeGroupCreateRequest,
    type OpenApiPromocodeGroupCreateResponseSuccess,
    OpenApiPromocodeGroupCreateUrl,
    type OpenApiPromocodeGroupDeletePathParameters,
    type OpenApiPromocodeGroupDeleteResponseSuccess,
    type OpenApiPromocodeGroupGetByIdPathParameters,
    type OpenApiPromocodeGroupGetByIdResponseSuccess,
    type OpenApiPromocodeGroupListResponseSuccess,
    type OpenApiPromocodeGroupListSearchParams,
    OpenApiPromocodeGroupListUrl,
    type OpenApiPromocodeGroupPromocodeCreatePathParameters,
    type OpenApiPromocodeGroupPromocodeCreateRequest,
    type OpenApiPromocodeGroupPromocodeCreateResponseSuccess,
    type OpenApiPromocodeGroupPromocodeDeletePathParameters,
    type OpenApiPromocodeGroupPromocodeListPathParameters,
    type OpenApiPromocodeGroupPromocodeListResponseSuccess,
    type OpenApiPromocodeGroupPromocodeListSearchParams,
    type OpenApiPromocodeGroupPromocodesImportPathParameters,
    type OpenApiPromocodeGroupPromocodesImportResponseSuccess,
    type OpenApiPromocodeGroupPromocodesImportSearchParams,
    type OpenApiPromocodeGroupPromocodeUpdatePathParameters,
    type OpenApiPromocodeGroupPromocodeUpdateRequest,
    type OpenApiPromocodeGroupPromocodeUpdateResponseSuccess,
    type OpenApiPromocodeGroupSetDeactivatedPathParameters,
    type OpenApiPromocodeGroupSetDeactivatedRequest,
    type OpenApiPromocodeGroupSetDeactivatedResponseSuccess,
    type OpenApiPromocodeGroupUpdatePathParameters,
    type OpenApiPromocodeGroupUpdateRequest,
    type OpenApiPromocodeGroupUpdateResponseSuccess,
    type OpenApiPromocodeListResponseSuccess,
    type OpenApiPromocodeListSearchParams,
    type OpenApiSchemaPromocodeGroup,
    type OpenApiSchemaPromocodeItem,
} from '#shared/api/schema';
import { InvalidateGridApiHelper } from '#shared/lib/requests';

const PromocodeGroupTag = 'PromocodeGroup' as const;

const promocodeGridTag = { type: PromocodeGroupTag, id: 'promocodeGrid' };
const promocodeCountTag = { type: PromocodeGroupTag, id: 'promocodeCount' };

export const promocodeGroupGridTagInvalidator = new InvalidateGridApiHelper<
    typeof PromocodeGroupTag,
    OpenApiPromocodeGroupListSearchParams
>(PromocodeGroupTag, 'promocodeGrid');

const getPromocodeGroupTag = ({ id }: Pick<OpenApiSchemaPromocodeGroup, 'id'>) => {
    return { type: PromocodeGroupTag, id };
};

function updatePromocodeGroupInGridCache(
    data: OpenApiPromocodeGroupListSearchParams,
    onPromocodeGroupUpdate: (currentGroup: OpenApiSchemaPromocodeGroup) => OpenApiSchemaPromocodeGroup | null
) {
    promocodeApi.endpoints.getList.updateQueryData(data, (draft) => {
        if (draft) {
            const newPromocodes: OpenApiSchemaPromocodeGroup[] = [];

            draft.data.forEach((promocode) => {
                const newPromocode = onPromocodeGroupUpdate(promocode);

                if (newPromocode) {
                    newPromocodes.push(newPromocode);
                }
            });
            draft.data = newPromocodes;
        }
    });
}

const PromocodeGroupCodesListTag = 'PromocodeGroupCodesList' as const;

const promocodeGroupCodesListTag = { type: PromocodeGroupCodesListTag, id: 'promocodeGroupCodesList' };

export const promocodeGroupCodesListTagInvalidator = new InvalidateGridApiHelper<
    typeof PromocodeGroupCodesListTag,
    {
        searchParams: OpenApiPromocodeGroupPromocodeListSearchParams;
        pathParams: OpenApiPromocodeGroupPromocodeListPathParameters;
    }
>(PromocodeGroupCodesListTag, 'promocodeGroupCodesList');

export const promocodeApi = createApi({
    name: 'entities/promocode/api/api',
    tagTypes: [PromocodeGroupTag, PromocodeGroupCodesListTag],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getList: builder.query<OpenApiPromocodeGroupListResponseSuccess, OpenApiPromocodeGroupListSearchParams>({
                providesTags: (result, error, args) => {
                    if (error || !result?.data) {
                        return [];
                    }

                    return [promocodeGridTag, ...promocodeGroupGridTagInvalidator.provideHashTagForGridEndpoint(args)];
                },
                query: (params) => {
                    return {
                        url: OpenApiPromocodeGroupListUrl,
                        params,
                        method: 'GET',
                    };
                },
            }),
            getById: builder.query<
                OpenApiPromocodeGroupGetByIdResponseSuccess,
                OpenApiPromocodeGroupGetByIdPathParameters
            >({
                query: ({ id }) => {
                    return { url: `/promocode_groups/${id}`, method: 'GET' };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        // общий тэг для инвалидации всех getById запросов
                        { type: PromocodeGroupTag, id: 'getPromocodeGroup' },
                        getPromocodeGroupTag(result),
                    ];
                },
            }),

            createPromocodeGroup: builder.mutation<
                OpenApiPromocodeGroupCreateResponseSuccess,
                OpenApiPromocodeGroupCreateRequest
            >({
                query: () => {
                    return {
                        url: OpenApiPromocodeGroupCreateUrl,
                        method: 'POST',
                    };
                },
                invalidatesTags: (error) => {
                    if (error) {
                        return [];
                    }

                    return [promocodeGridTag, promocodeCountTag];
                },
            }),
            update: builder.mutation<
                OpenApiPromocodeGroupUpdateResponseSuccess,
                { params: OpenApiPromocodeGroupUpdatePathParameters; payload: OpenApiPromocodeGroupUpdateRequest }
            >({
                query: ({ params, payload }) => {
                    return {
                        url: getOpenApiPromocodeGroupUpdateUrl(params),
                        method: 'PUT',
                        body: payload,
                    };
                },
                invalidatesTags: (result, error, request) => {
                    if (error || !result?.isSuccess) {
                        return [];
                    }

                    return [promocodeGridTag, promocodeCountTag, getPromocodeGroupTag({ id: request.params.id })];
                },
            }),
            changeDeactivated: builder.mutation<
                OpenApiPromocodeGroupSetDeactivatedResponseSuccess,
                {
                    params: OpenApiPromocodeGroupSetDeactivatedPathParameters;
                    payload: OpenApiPromocodeGroupSetDeactivatedRequest;
                }
            >({
                query: ({ params, payload }) => {
                    return {
                        url: getOpenApiPromocodeGroupSetDeactivatedUrl(params),
                        method: 'PATCH',
                        body: payload,
                    };
                },
                invalidatesTags: (result, error, { params: { id } }) => {
                    if (error || !result?.isSuccess) {
                        return [];
                    }

                    return [...promocodeGroupGridTagInvalidator.getTagsToInvalidate(), getPromocodeGroupTag({ id })];
                },
                async onQueryStarted({ params: { id }, payload }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const {
                        data: { isSuccess, status },
                    } = queryFulfilledResult;

                    if (!isSuccess) {
                        return;
                    }

                    const deactivated = payload.deactivated;

                    promocodeApi.endpoints.getById.patchQueryData({ id }, [
                        { op: 'replace', path: ['deactivated'], value: deactivated },
                    ]);

                    if (promocodeGroupGridTagInvalidator.lastArgs) {
                        updatePromocodeGroupInGridCache(promocodeGroupGridTagInvalidator.lastArgs, (draft) => {
                            if (draft.id === id) {
                                const updated = {
                                    ...draft,
                                    deactivated,
                                };

                                if (status) {
                                    updated.status = status;
                                }

                                return updated;
                            }

                            return draft;
                        });
                    }
                },
            }),
            deleteGroup: builder.mutation<
                OpenApiPromocodeGroupDeleteResponseSuccess,
                OpenApiPromocodeGroupDeletePathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiPromocodeGroupDeleteUrl(params),
                        method: 'DELETE',
                    };
                },
                invalidatesTags: (_, error, { id }) => {
                    if (error) {
                        return [];
                    }

                    // Если lastArgs null, значит, что Grid прямо сейчас не виден в DOM, инвалидируем его целиком
                    if (!promocodeGroupGridTagInvalidator.lastArgs) {
                        return [promocodeGridTag, promocodeCountTag, getPromocodeGroupTag({ id })];
                    }

                    return [
                        ...promocodeGroupGridTagInvalidator.getTagsToInvalidate(),
                        promocodeCountTag,
                        getPromocodeGroupTag({ id }),
                    ];
                },
                async onQueryStarted({ id }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    if (promocodeGroupGridTagInvalidator.lastArgs) {
                        updatePromocodeGroupInGridCache(promocodeGroupGridTagInvalidator.lastArgs, (draft) => {
                            if (draft.id === id) {
                                return null;
                            }

                            return draft;
                        });
                    }
                },
            }),
            getCount: builder.query<OpenApiGetIncentivesCountResponseSuccess, OpenApiGetIncentivesCountRequest>({
                query: () => {
                    return {
                        url: '/incentives/count',
                        method: 'GET',
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [promocodeCountTag];
                },
            }),
            getGroupCodesList: builder.query<
                OpenApiPromocodeGroupPromocodeListResponseSuccess,
                {
                    pathParams: OpenApiPromocodeGroupPromocodeListPathParameters;
                    searchParams: OpenApiPromocodeGroupPromocodeListSearchParams;
                }
            >({
                providesTags: (result, error, args) => {
                    if (error || !result?.items) {
                        return [];
                    }

                    return [
                        promocodeGroupCodesListTag,
                        ...promocodeGroupCodesListTagInvalidator.provideHashTagForGridEndpoint(args),
                    ];
                },
                query: ({ pathParams, searchParams }) => {
                    return {
                        url: getOpenApiPromocodeGroupPromocodeListUrl(pathParams),
                        params: searchParams,
                        method: 'GET',
                    };
                },
            }),
            createCode: builder.mutation<
                OpenApiPromocodeGroupPromocodeCreateResponseSuccess,
                {
                    pathParams: OpenApiPromocodeGroupPromocodeCreatePathParameters;
                    payload: OpenApiPromocodeGroupPromocodeCreateRequest;
                }
            >({
                query: ({ pathParams, payload }) => {
                    return {
                        url: getOpenApiPromocodeGroupPromocodeCreateUrl(pathParams),
                        method: 'POST',
                        body: payload,
                    };
                },
                invalidatesTags: (result, error) => {
                    if (error || !result || !result.isSuccess) {
                        return [];
                    }

                    return invalidatePromocodeGroupCodesGridCache();
                },
            }),
            updateCode: builder.mutation<
                OpenApiPromocodeGroupPromocodeUpdateResponseSuccess,
                {
                    pathParams: OpenApiPromocodeGroupPromocodeUpdatePathParameters;
                    payload: OpenApiPromocodeGroupPromocodeUpdateRequest;
                }
            >({
                query: ({ pathParams, payload }) => {
                    return {
                        url: getOpenApiPromocodeGroupPromocodeUpdateUrl(pathParams),
                        method: 'PATCH',
                        body: payload,
                    };
                },
                invalidatesTags: (result, error) => {
                    if (error || !result || !result.isSuccess) {
                        return [];
                    }

                    if (!promocodeGroupCodesListTagInvalidator.lastArgs) {
                        return invalidatePromocodeGroupCodesGridCache();
                    }

                    return [];
                },
                async onQueryStarted(
                    { pathParams: { promocode_id: promocodeId }, payload: { code: newCode } },
                    { queryFulfilled }
                ) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    if (!data) {
                        return;
                    }

                    const lastArgs = promocodeGroupCodesListTagInvalidator.lastArgs;

                    if (!lastArgs) {
                        return;
                    }

                    updatePromocodeCodeInGridCache(lastArgs, (item) => {
                        if (item.id === promocodeId) {
                            return { ...item, code: newCode };
                        }

                        return item;
                    });
                },
            }),
            deleteCode: builder.mutation<void, { pathParams: OpenApiPromocodeGroupPromocodeDeletePathParameters }>({
                query: ({ pathParams }) => {
                    return {
                        url: getOpenApiPromocodeGroupPromocodeDeleteUrl(pathParams),
                        method: 'DELETE',
                    };
                },
                invalidatesTags: (_result, error) => {
                    if (error) {
                        return [];
                    }

                    return invalidatePromocodeGroupCodesGridCache();
                },
            }),
            importPromocodeGroupFromExcel: builder.mutation<
                OpenApiPromocodeGroupPromocodesImportResponseSuccess,
                {
                    payload: FormData;
                    pathParams: OpenApiPromocodeGroupPromocodesImportPathParameters;
                    searchParams: OpenApiPromocodeGroupPromocodesImportSearchParams;
                }
            >({
                invalidatesTags: (result, error, { searchParams }) => {
                    if (error || !result || !searchParams.do_import) {
                        return [];
                    }

                    return invalidatePromocodeGroupCodesGridCache();
                },
                queryFn: async ({ payload, pathParams, searchParams }, api, extraOptions) => {
                    const formDataBaseQuery = createBaseQuery(apiFormDataRequest);

                    const { data, error } = await formDataBaseQuery(
                        {
                            url: getOpenApiPromocodeGroupPromocodesImportUrl(pathParams),
                            params: searchParams,
                            method: 'POST',
                            body: payload,
                        },
                        api,
                        extraOptions
                    );

                    if (error) {
                        return { error: error };
                    }

                    return { data: data as OpenApiPromocodeGroupPromocodesImportResponseSuccess };
                },
            }),
            getPromocodesList: builder.query<OpenApiPromocodeListResponseSuccess, OpenApiPromocodeListSearchParams>({
                query: (params) => {
                    return {
                        url: `/promocodes`,
                        params,
                        method: 'GET',
                    };
                },
            }),
            getPromocodeById: builder.query<
                OpenApiPromocodeGetByIdResponseSuccess,
                OpenApiPromocodeGetByIdPathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiPromocodeGetByIdUrl(params),
                        method: 'GET',
                    };
                },
            }),
        };
    },
});

function updatePromocodeCodeInGridCache(
    query: {
        pathParams: OpenApiPromocodeGroupPromocodeListPathParameters;
        searchParams: OpenApiPromocodeGroupPromocodeListSearchParams;
    },
    onItemUpdate: (item: OpenApiSchemaPromocodeItem) => OpenApiSchemaPromocodeItem | null
) {
    promocodeApi.endpoints.getGroupCodesList.updateQueryData(query, (draft) => {
        if (!draft) {
            return;
        }

        const newItems: OpenApiSchemaPromocodeItem[] = [];

        draft.items.forEach((item) => {
            const updatedItem = onItemUpdate(item);

            if (updatedItem) {
                newItems.push(updatedItem);
            }
        });

        draft.items = newItems;
    });
}

function invalidatePromocodeGroupCodesGridCache() {
    return [promocodeGroupCodesListTag, ...promocodeGroupCodesListTagInvalidator.getTagsToInvalidate()];
}

export const useGetPromocodeGroupById = promocodeApi.useLazyGetByIdQuery;
export const getPromocodeGroupById = promocodeApi.endpoints.getById;
export const useGetPromocodesCount = promocodeApi.useGetCountQuery;
export const invalidateCachePromocodesCount = () => promocodeApi.util.invalidateTags([promocodeCountTag]);
export const createPromocodeGroup = promocodeApi.endpoints.createPromocodeGroup.mutate;
export const updatePromocodeGroup = promocodeApi.endpoints.update.mutate;
export const changePromocodeDeactivated = promocodeApi.endpoints.changeDeactivated.mutate;
export const useDeletePromocodeGroup = promocodeApi.useDeleteGroupMutation;

export const useGetPromocodeGroupCodesList = promocodeApi.useGetGroupCodesListQuery;
export const getPromocodeGroupCodesList = promocodeApi.endpoints.getGroupCodesList;
export const createPromocodeGroupCode = promocodeApi.endpoints.createCode.mutate;
export const updatePromocodeGroupCode = promocodeApi.endpoints.updateCode.mutate;
export const deletePromocodeGroupCode = promocodeApi.endpoints.deleteCode.mutate;
export const importPromocodeGroupFromExcel = promocodeApi.endpoints.importPromocodeGroupFromExcel.mutate;
export const useGetPromocodesList = promocodeApi.useGetPromocodesListQuery;
export const useGetPromocodeByIdQuery = promocodeApi.useGetPromocodeByIdQuery;
